import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useParams } from '../../../contexts/RouterContext';
import ProjectGroupsModule from '../../../redux/project_grouping';
import { nuc, openInNewTab } from '../../../utils';
import { isReleaseGroupScope, type ReportsV3Params, type ReportV3Format } from '../types';

import { useRemediationOptionsParams } from './useRemediationOptionsParams';

/**
 * Hook that returns a callback to download a remediation guidance report by
 * opening the download endpoint in a new tab. Same pattern as `useDownloadReport`
 * but targets the remediation-guidance endpoints.
 *
 * `bundle=true` makes the backend respond with a ZIP containing the rendered report
 * plus the supplemental JSON, which is what the Download button is meant to deliver.
 */
export function useRemediationDownloadReport(format: ReportV3Format) {
  const params = useParams<ReportsV3Params>();
  const isReleaseGroup = isReleaseGroupScope(params);
  const selectedReleaseId = useSelector(ProjectGroupsModule.selectors.getSelectedReleaseId);
  const optionsParams = useRemediationOptionsParams();

  const url = useMemo(() => {
    const optionsSuffix = optionsParams ? `&${optionsParams}` : '';

    if (isReleaseGroup) {
      const { projectGroupId, releaseId } = params;
      const resolvedReleaseId = releaseId ?? selectedReleaseId ?? 0;
      return `/api/project_group/${projectGroupId}/release/${resolvedReleaseId}/report/remediation-guidance?bundle=true&format=${format}${optionsSuffix}`;
    }

    const projectLocator = nuc(`${params.project_id}$${params.revision_id}`);
    return `/api/revisions/${projectLocator}/report/remediation-guidance?bundle=true&format=${format}${optionsSuffix}`;
  }, [format, isReleaseGroup, optionsParams, params, selectedReleaseId]);

  const downloadReport = useCallback(() => {
    openInNewTab(url);
  }, [url]);

  return { downloadReport };
}
